import { useResizableColumns, ResizableTable, ResizableTh } from "@/components/ui/resizable-table";   // Step 11d · add-on: adjustable columns
import React, { useEffect, useState } from "react";
import useEffectEvent from "@/lib/useEffectEvent";   // Step 7c · E3: the effect runs on its own deps only (see the file)
import { fmtDate } from "@/lib/dt";
import RateHistoryRow from "@/components/billing/RateHistoryRow";   // RATE HISTORY VOID
import api, { toErrMsg } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { useReviewRoles } from "@/lib/reviewRoles";   // STEP 3 · Rule 8: the server's role sets
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Upload, Copy, Trash2, UserPlus, Palette, KeyRound, RefreshCw, History, PanelLeft, PanelTop } from "lucide-react";
import { toast } from "sonner";
import TwoFactorSection from "@/components/TwoFactorSection";
import PlanRequestSection from "@/components/PlanRequestSection";
import SignInLocksPanel from "@/components/SignInLocksPanel";   // Batch 22 · PR B · Follow-up 2
import FirmReportSettingsCard from "@/components/FirmReportSettingsCard";   // Batch 33 · S12
import { askConfirm } from "@/lib/appDialog";   // Batch 15 · N4 (F-118)
import { fmtNumber } from "@/components/Amount";   // STEP 3 · rule 2 · B-2e: the one amount formatter


// -------------------- Navigation Layout preference --------------------
// Secondary control for the Side ↔ Top layout switch. The primary
// control is the circular arrow button in the sidebar/top-nav; both
// drive the same `preferences.nav_layout` value on the user record.
function NavigationLayoutCard() {
  const { user, refreshUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const current = user?.preferences?.nav_layout === "top" ? "top" : "side";
  const set = async (next) => {
    if (next === current) return;
    setSaving(true);
    try {
      await api.patch("/auth/me/preferences", { nav_layout: next });
      try { localStorage.setItem("af_nav_layout", next); } catch { /* noop */ }
      // Refresh AuthContext so the arrow-button icon flips in sync.
      if (typeof refreshUser === "function") await refreshUser();
      toast.success(`Navigation switched to ${next === "top" ? "Top" : "Side"} layout`);
    } catch (e) {
      toast.error(toErrMsg(e, "Failed to save layout preference"));
    } finally { setSaving(false); }
  };
  return (
    <Card data-testid="settings-nav-layout-card">
      <CardHeader>
        <div className="flex items-center gap-2">
          <PanelLeft className="h-4 w-4 text-slate-500" />
          <CardTitle className="text-base">Navigation layout</CardTitle>
        </div>
        <p className="text-[11px] text-muted-foreground">
          Choose how the app's navigation appears in your account. This is
          your personal preference and stays with you across devices.
        </p>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <button type="button" disabled={saving} onClick={() => set("side")}
            data-testid="nav-layout-side"
            className={`flex items-start gap-3 rounded-lg border p-4 text-start transition ${
              current === "side"
                ? "border-emerald-500 bg-emerald-50 ring-1 ring-emerald-500/30 dark:bg-emerald-950/30"
                : "hover:border-slate-300 hover:bg-slate-50 dark:hover:bg-slate-900/40"
            }`}>
            <PanelLeft className="mt-0.5 h-5 w-5 text-slate-600" />
            <div className="min-w-0">
              <div className="text-sm font-semibold">Side (vertical)</div>
              <div className="mt-0.5 text-[11px] text-muted-foreground">
                Sidebar on the left with 13 collapsible groups. Default.
              </div>
            </div>
            {current === "side" && (
              <span className="ms-auto rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] font-medium text-white">
                Active
              </span>
            )}
          </button>
          <button type="button" disabled={saving} onClick={() => set("top")}
            data-testid="nav-layout-top"
            className={`flex items-start gap-3 rounded-lg border p-4 text-start transition ${
              current === "top"
                ? "border-emerald-500 bg-emerald-50 ring-1 ring-emerald-500/30 dark:bg-emerald-950/30"
                : "hover:border-slate-300 hover:bg-slate-50 dark:hover:bg-slate-900/40"
            }`}>
            <PanelTop className="mt-0.5 h-5 w-5 text-slate-600" />
            <div className="min-w-0">
              <div className="text-sm font-semibold">Top (horizontal)</div>
              <div className="mt-0.5 text-[11px] text-muted-foreground">
                Group names render as a horizontal bar under the top toolbar. Each opens a dropdown.
              </div>
            </div>
            {current === "top" && (
              <span className="ms-auto rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] font-medium text-white">
                Active
              </span>
            )}
          </button>
        </div>
      </CardContent>
    </Card>
  );
}

// Review-Points seniority ladder (Mar 2026): Supervisor → Assistant Manager
// → Manager → Senior Manager sit between Preparer and Partner. Legacy
// "Reviewer" / "Audit Manager" / "Auditor" remain assignable so existing
// users keep working per the "leave existing users as-is" migration call.
// Batch 6 · B (AT-32): stored role values stay; the screen names "EQC" the EQ reviewer
// PR 44 item 8 (owner 18-Oct-2026): "Director" is a display title of the Partner role — stored as role Partner + title
// Director (backend user_titles.py); every Partner right applies unchanged.
const ROLE_LABELS = { EQC: "EQ reviewer", Director: "Partner / Audit Director" };
const ROLES = [
  "Partner", "Director", "Senior Manager", "Manager", "Assistant Manager", "Supervisor",
  "Reviewer", "EQC", "Preparer", "Audit Manager", "Auditor", "Admin",
];

// Loads the org logo via a short-lived download-scoped token (audit
// fix H2 — never embed the raw session JWT in <img src>).
function SettingsLogoImg({ logoUrl }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const m = String(logoUrl || "").match(/documents\/file\/([^/?]+)/);
    if (!m) { setSrc(logoUrl?.startsWith("http") ? logoUrl : null); return; }
    import("@/lib/api").then(({ getDownloadUrl }) =>
      getDownloadUrl(m[1], { mode: "preview" })
        .then((full) => { if (!cancelled) setSrc(full); })
        .catch(() => { if (!cancelled) setSrc(null); }));
    return () => { cancelled = true; };
  }, [logoUrl]);
  if (!src) return <div className="h-20 w-20 rounded-md border bg-slate-100" />;
  return <img src={src} alt="logo" className="h-20 w-20 rounded-md border object-cover" />;
}


export default function Settings() {
  const colW4 = useResizableColumns({ storageKey: "settings-invites", defaults: [240, 110, 110, 130], mins: [130, 72, 72, 90] });   // Step 11d · add-on
  const colW3 = useResizableColumns({ storageKey: "settings-users", defaults: [240, 240, 110, 110, 130], mins: [130, 130, 72, 72, 90] });   // Step 11d · add-on
  const colW2 = useResizableColumns({ storageKey: "settings-rate-history", defaults: [130, 160, 130, 130, 130], mins: [96, 100, 90, 90, 90] });   // Step 11d · add-on
  const colW = useResizableColumns({ storageKey: "settings-rate-card", defaults: [110, 130, 160, 160, 100], mins: [72, 90, 100, 100, 60] });   // Step 11d · add-on
  const { t } = useI18n();
  const { user, org, refreshOrg } = useAuth();
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [branding, setBranding] = useState({ name: "" });
  const [tz, setTz] = useState("");  // Round 27 · X3 firm time zone
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: "", role: "Auditor" });
  const [inviteOpen, setInviteOpen] = useState(false);
  const [creatingInvite, setCreatingInvite] = useState(false);

  useEffect(() => {
    // Round 7 · P2 — Platform Admin isn't a firm admin in any tenant.
    // The firm-admin-only endpoints below return 403 and used to
    // bubble up as (a) the dreaded "Firm-admin access required" toast
    // from api.js's shared interceptor and (b) an uncaught promise
    // rejection on the first .then() that had no .catch, which the
    // CRA dev overlay surfaces as "Uncaught runtime errors". We now
    // fetch silently and swallow 403 (and only 403 — a real server
    // error still surfaces). The firm-admin sections themselves are
    // further gated by `canBrand` / `canManage` / `canCrudUsers`
    // below, so a Platform Admin sees a read-only notice instead of
    // broken inputs.
    api.get("/users", { _silent: true })
       .then((r) => setUsers(r.data))
       .catch(() => setUsers([]));
    api.get("/invites", { _silent: true })
       .then((r) => setInvites(r.data))
       .catch(() => setInvites([]));
  }, []);

  const seedFromOrg = useEffectEvent(() => {
    if (org) setTz(org.time_zone || "");
    if (org) setBranding({
      name: org.name || "",
    });
  });
  // Step 7c · E3: seeded when the firm / its name changes (as before), never on a new org object (a refresh keeps the typing)
  useEffect(() => { seedFromOrg(); }, [org?.id, org?.name, seedFromOrg]);

  const saveBranding = async () => {
    try {
      // Colours are locked platform-wide; only save name (logo is a
      // separate upload endpoint). Strip the frozen palette from the
      // payload so a stale in-memory value can never bleed to the API.
      await api.patch("/organization", { name: branding.name, ...(tz ? { time_zone: tz } : {}) });
      await refreshOrg();
      toast.success(t("settings.brandingSaved"));
    } catch (e) {
      toast.error(toErrMsg(e, "Failed"));
    }
  };

  const uploadLogo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post("/organization/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refreshOrg();
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(toErrMsg(err, "Failed"));
    } finally { setUploadingLogo(false); }
  };

  const createInvite = async () => {
    setCreatingInvite(true);
    try {
      const r = await api.post("/invites", inviteForm);
      if (r.data.email_sent) {
        toast.success(`${t("settings.emailSentTo")} ${inviteForm.email}`);
      } else {
        // Batch 17 · F-139: the link is in the reply only on a dev / local copy; on live the e-mail is the only channel
        if (r.data.token) {
          const url = `${window.location.origin}/accept-invite?token=${r.data.token}`;
          try { await navigator.clipboard.writeText(url); } catch { /* ignore */ }
        }
        toast.success(t("settings.inviteCreated"));
        toast.warning(t("settings.emailNotSent"));       // Batch 18 · Stage 1: the honest message (EN / AR); Resend in the table
      }
      setInviteOpen(false);
      setInviteForm({ email: "", role: "Auditor" });
      const list = await api.get("/invites");
      setInvites(list.data);
    } catch (e) {
      toast.error(toErrMsg(e, "Failed"));
    } finally { setCreatingInvite(false); }
  };

  const copyLink = async (token) => {
    const url = `${window.location.origin}/accept-invite?token=${token}`;
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); }
    catch { toast.error("Copy failed"); }
  };

  // Batch 18 · Add-on 1 (O3): the action reads "Send to <e-mail>" — a long address is shortened in the middle on the button, the full
  // address sits in the tooltip, and the address stays left-to-right inside Arabic text (<bdi dir="ltr">).
  const shortEmail = (e, max = 24) => { const s = String(e || ""); if (s.length <= max) return s; const keep = Math.max(4, Math.floor((max - 1) / 2)); return `${s.slice(0, keep)}…${s.slice(-keep)}`; };
  const sendToLabel = (email) => { const [before, after] = t("settings.resendInvite").split("{email}"); return (<>{before}<bdi dir="ltr">{shortEmail(email)}</bdi>{after ?? ""}</>); };
  const resendInvite = async (id, email) => {
    // Batch 18 · Stage 1: a new token (the old link is revoked), the e-mail again, an audit row; the reply says what happened
    try {
      const r = await api.post(`/invites/${id}/resend`);
      if (r.data.email_sent) toast.success(t("settings.inviteResent").replace("{email}", email || r.data.email || "")); else toast.warning(t("settings.emailNotSent"));
      const list = await api.get("/invites");
      setInvites(list.data);
    } catch (e) { toast.error(toErrMsg(e, "Failed")); }
  };

  const revoke = async (id) => {
    await api.delete(`/invites/${id}`);
    const list = await api.get("/invites");
    setInvites(list.data);
    toast.success("Revoked");
  };

  // ---- User CRUD ----
  const [editingUser, setEditingUser] = useState(null); // {id, name, role}
  const [savingUser, setSavingUser] = useState(false);
  const [creatingUser, setCreatingUser] = useState(false);
  const [newUserOpen, setNewUserOpen] = useState(false);
  const [newUser, setNewUser] = useState({ email: "", name: "", role: "Preparer", billing_grade: "", password: "" });
  // STEP 3 · B-8b: the server's own sets (GET /review-roles): invite = POST /invites; the pending list = GET /invites
  const { can: canRole } = useReviewRoles();
  const canInvite = canRole("invite.create", user?.role);
  const canManageInvites = canRole("invite.manage", user?.role);
  const canCrudUsers = user?.role === "Admin" || user?.role === "Partner";
  const canBrand = user?.role === "Admin" || user?.role === "Partner";
  const canRateCard = user?.role === "Admin" || user?.role === "Partner";

  // Billing grade list (available to everyone as a dropdown source; Partner can edit)
  const [grades, setGrades] = useState([]);
  const [newGrade, setNewGrade] = useState({ grade: "", hourly_rate: "", currency: "" });   // Dashboard 2.5: currency per grade
  const [gradeSaving, setGradeSaving] = useState(false);
  const loadGrades = async () => {
    try { const r = await api.get("/billing/grades"); setGrades(r.data || []); }
    catch { /* leave list empty on failure */ }
  };
  useEffect(() => { loadGrades(); }, []);
  const upsertGrade = async () => {
    if (!newGrade.grade.trim()) { toast.error("Grade name required"); return; }
    setGradeSaving(true);
    try {
      await api.post("/billing/grades", {
        grade: newGrade.grade.trim(),
        hourly_rate: Number(newGrade.hourly_rate) || 0,
        currency: (newGrade.currency || "").trim(),
      });
      toast.success("Grade saved");
      setNewGrade({ grade: "", hourly_rate: "", currency: "" });
      await loadGrades();
    } catch (e) { toast.error(toErrMsg(e, "Failed")); }
    finally { setGradeSaving(false); }
  };
  const deleteGrade = async (g) => {
    if (!await askConfirm(`Delete grade "${g}"?`)) return;
    try {
      await api.delete(`/billing/grades/${encodeURIComponent(g)}`);
      toast.success("Deleted"); await loadGrades();
    } catch (e) { toast.error(toErrMsg(e, "Failed")); }
  };

  // Rate card history (per grade)
  const [historyGrade, setHistoryGrade] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  const openHistory = async (g) => {
    setHistoryGrade(g);
    setHistoryRows([]);
    try {
      const r = await api.get(`/billing/grades/${encodeURIComponent(g)}/history`);
      setHistoryRows(r.data || []);
    } catch (e) { toast.error(toErrMsg(e, "Failed to load history")); }
  };

  const saveUserEdit = async () => {
    if (!editingUser) return;
    setSavingUser(true);
    try {
      await api.patch(`/users/${editingUser.id}`,
                       { name: editingUser.name, role: editingUser.role,
                         billing_grade: editingUser.billing_grade || "" });
      toast.success("User updated");
      const r = await api.get("/users"); setUsers(r.data);
      setEditingUser(null);
    } catch (e) {
      toast.error(toErrMsg(e, "Update failed"));
    } finally { setSavingUser(false); }
  };

  const deleteUser = async (u) => {
    if (u.id === user.id) { toast.error("You cannot delete your own account."); return; }
    if (!await askConfirm(`Delete user "${u.name}" (${u.email})?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User deleted");
      const r = await api.get("/users"); setUsers(r.data);
    } catch (e) {
      toast.error(toErrMsg(e, "Delete failed"));
    }
  };

  const reset2FA = async (u) => {
    if (!await askConfirm(`Send an administrator-issued sign-in code to ${u.email}?\n\nUse this only if the user has lost access to their email and all backup codes.`)) return;
    try {
      const r = await api.post("/admin/2fa/reset", { user_email: u.email }, { _silent: true });
      if (r.data.dev_code) {
        toast.success(`Reset code (dev fallback): ${r.data.dev_code}`);
      } else if (r.data.delivery_failed) {
        toast.warning(`Reset code issued but email delivery failed. Please retry.`);
      } else {
        toast.success(`Reset code emailed to ${u.email}`);
      }
    } catch (e) {
      toast.error(toErrMsg(e, "Failed to send reset code"));
    }
  };

  const createUserDirect = async () => {
    if (!newUser.email || !newUser.name) { toast.error("Email + name required"); return; }
    setCreatingUser(true);
    try {
      const r = await api.post("/users", newUser);
      if (r.data.temp_password) {
        toast.success(`Created — temp password: ${r.data.temp_password}`);
      } else {
        toast.success("User created");
      }
      setNewUserOpen(false);
      setNewUser({ email: "", name: "", role: "Preparer", billing_grade: "", password: "" });
      const list = await api.get("/users"); setUsers(list.data);
    } catch (e) {
      toast.error(toErrMsg(e, "Create failed"));
    } finally { setCreatingUser(false); }
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{t("settings.title")}</h1>
      </div>

      {/* Round 7 · P2 — Platform-Admin-only notice. Platform Admin
          isn't a firm admin in any tenant, so the firm-branding +
          team sections below are naturally read-only for that role
          (every button that writes is gated by canBrand / canManage
          / canCrudUsers, which are all false here). Instead of
          leaving the user guessing why "Save" and "Create user"
          don't appear, surface a bilingual notice at the top. The
          actual data fetches (`/users`, `/invites`) are swallowed
          silently so the dev-overlay 403 crash can't recur. */}
      {user?.role === "Platform Admin" && (
        <div className="rounded-md border border-sky-300 bg-sky-50 p-3 text-xs text-sky-900 dark:bg-sky-950/30 dark:border-sky-800 dark:text-sky-200"
             data-testid="settings-platform-admin-notice">
          <div className="font-semibold">
            Platform Admin — firm-admin sections are read-only here.
            {" · "}
            <span dir="rtl">
              مشرف المنصّة — أقسام مشرف الشركة للقراءة فقط هنا.
            </span>
          </div>
          <div className="mt-1 opacity-90">
            Manage per-tenant settings (branding, team, invites) from
            the Platform page → the specific tenant. This page shows
            your personal preferences (2FA, nav layout).
          </div>
        </div>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Palette className="h-4 w-4 text-slate-500" />
            <CardTitle className="text-base">{t("settings.branding")}</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="md:col-span-1">
            <Label>{t("settings.logo")}</Label>
            <div className="mt-2 flex flex-col items-start gap-2">
              {org?.logo_url ? (
                <SettingsLogoImg logoUrl={org.logo_url} />
              ) : (
                <div className="flex h-20 w-20 items-center justify-center rounded-md border bg-slate-100 text-xs text-muted-foreground">
                  {t("settings.noLogo")}
                </div>
              )}
              {canBrand && (
                <label className="inline-flex cursor-pointer items-center gap-1 rounded-md border bg-white px-3 py-1.5 text-xs font-medium hover:bg-slate-50">
                  <Upload className="h-3 w-3" />
                  {uploadingLogo ? t("common.loading") : t("settings.uploadLogo")}
                  <input type="file" accept="image/*" className="hidden" onChange={uploadLogo} disabled={uploadingLogo} data-testid="upload-logo" />
                </label>
              )}
            </div>
          </div>
          <div className="md:col-span-2 space-y-3">
            <div>
              <Label>{t("settings.orgName")}</Label>
              <Input value={branding.name} onChange={(e) => setBranding({ ...branding, name: e.target.value })} disabled={!canBrand} data-testid="brand-name" />
            </div>
            {/* Batch 19 · A1 (F-141): the locked-palette line was removed on the owner's call; the colours stay fixed. */}
            <div className="mt-3 text-xs" data-testid="firm-time-zone-block">
              <label className="font-medium">{t("settings.timeZone")}</label>
              <input className="mt-1 w-full rounded border p-1 font-mono" list="tz-list" disabled={!canBrand} placeholder={Intl.DateTimeFormat().resolvedOptions().timeZone}
                     value={tz} onChange={(e) => setTz(e.target.value)} data-testid="firm-time-zone" />
              <datalist id="tz-list">{["Asia/Riyadh", "Asia/Karachi", "Asia/Dubai", "Europe/London", "America/New_York", "Asia/Kolkata", "Africa/Cairo"].map((z) => <option key={z} value={z} />)}</datalist>
              <div className="text-[10px] text-muted-foreground">{t("settings.timeZoneHelp")}</div>
            </div>
            {canBrand && (
              <Button onClick={saveBranding} data-testid="brand-save">
                {t("common.save")}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <FirmReportSettingsCard canEdit={canBrand} />

      <TwoFactorSection />

      <NavigationLayoutCard />

      {canRateCard && (
        <Card data-testid="ratecard-section">
          <CardHeader>
            <CardTitle className="text-base">Billing Grade Rate Card</CardTitle>
            <p className="text-[11px] text-muted-foreground" data-testid="ratecard-hint">
              {t("dsBilling.cardHint")}
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 gap-2 md:grid-cols-5 items-end rounded-md border bg-slate-50 p-2 dark:bg-slate-900/40">
              <div className="md:col-span-2">
                <Label className="text-xs">Grade / Title (e.g. Senior Manager)</Label>
                <Input value={newGrade.grade} data-testid="ratecard-new-grade"
                       onChange={(e) => setNewGrade({ ...newGrade, grade: e.target.value })}
                       placeholder="Partner · Director · Senior Manager · Manager · Senior · Staff …" />
              </div>
              <div>
                <Label className="text-xs">Hourly rate</Label>
                <Input type="number" min="0" step="0.01" value={newGrade.hourly_rate}
                       onChange={(e) => setNewGrade({ ...newGrade, hourly_rate: e.target.value })}
                       data-testid="ratecard-new-rate" placeholder="e.g. 250" />
              </div>
              <div>
                <Label className="text-xs" title={t("dsBilling.currencyHint")}>{t("dsBilling.currency")}</Label>
                <Input value={newGrade.currency} maxLength={10}
                       onChange={(e) => setNewGrade({ ...newGrade, currency: e.target.value })}
                       data-testid="ratecard-new-currency" placeholder="SAR" />
              </div>
              <Button onClick={upsertGrade} disabled={gradeSaving}
                      data-testid="ratecard-save"
                      className="bg-slate-900 hover:bg-slate-800">
                {gradeSaving ? "Saving…" : "Add / Update"}
              </Button>
            </div>
            <ResizableTable cols={colW} wrap className="w-full text-sm">
              <thead className="border-b bg-slate-50 text-xs uppercase text-muted-foreground dark:bg-slate-900/60">
                <tr>
                  <ResizableTh index={0} className="px-3 py-2 text-start">Grade</ResizableTh>
                  <ResizableTh index={1} className="px-3 py-2 text-end">Hourly Rate</ResizableTh>
                  <ResizableTh index={2} className="px-3 py-2 text-start">{t("dsBilling.currency")}</ResizableTh>
                  <ResizableTh index={3} className="px-3 py-2 text-start">{t("dsBilling.effectiveFrom")}</ResizableTh>
                  <ResizableTh index={4} className="px-3 py-2 text-end"></ResizableTh>
                </tr>
              </thead>
              <tbody>
                {grades.length === 0 && (
                  <tr><td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">No grades yet. Add one above.</td></tr>
                )}
                {grades.map((g) => (
                  <tr key={g.grade} className="border-b" data-testid={`ratecard-row-${g.grade}`}>
                    <td className="px-3 py-2 font-medium">{g.grade}</td>
                    <td className="px-3 py-2 num-cell">{fmtNumber(Number(g.hourly_rate || 0))}</td>
                    <td className="px-3 py-2 text-xs" data-testid={`ratecard-currency-${g.grade}`}>{g.currency || t("s11d2.engagementCurrency")}</td>   {/* Step 11d-2 · X5: no currency = the engagement's */}
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {(g.rate_effective_from || g.updated_at) ? fmtDate(g.rate_effective_from || g.updated_at) : "—"}
                      {g.updated_by_name ? <> by {g.updated_by_name}</> : null}
                    </td>
                    <td className="px-3 py-2 text-end">
                      <Button size="sm" variant="ghost" onClick={() => openHistory(g.grade)}
                              title="Rate revision history"
                              data-testid={`ratecard-history-${g.grade}`}>
                        <History className="h-3.5 w-3.5 text-emerald-600" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteGrade(g.grade)}
                              data-testid={`ratecard-delete-${g.grade}`}>
                        <Trash2 className="h-3.5 w-3.5 text-red-600" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </ResizableTable>
          </CardContent>
        </Card>
      )}

      {canRateCard && (
        <Dialog open={!!historyGrade} onOpenChange={(v) => !v && setHistoryGrade(null)}>
          <DialogContent data-testid="ratecard-history-dialog">
            <DialogHeader>
              <DialogTitle>Rate revisions — {historyGrade}</DialogTitle>
            </DialogHeader>
            <div className="max-h-96 overflow-y-auto">
              {historyRows.length === 0 && (
                <div className="p-4 text-center text-sm text-muted-foreground">No revisions yet.</div>
              )}
              {historyRows.length > 0 && (
                <ResizableTable cols={colW2} wrap className="w-full text-sm">
                  <thead className="border-b bg-slate-50 text-xs uppercase text-muted-foreground">
                    <tr>
                      <ResizableTh index={0} className="px-3 py-2 text-start">When</ResizableTh>
                      <ResizableTh index={1} className="px-3 py-2 text-start">By</ResizableTh>
                      <ResizableTh index={2} className="px-3 py-2 text-end">Old</ResizableTh>
                      <ResizableTh index={3} className="px-3 py-2 text-end">New</ResizableTh>
                      <ResizableTh index={4} className="px-3 py-2 text-end">Δ</ResizableTh>
                    </tr>
                  </thead>
                  <tbody>
                    {historyRows.map((h) => <RateHistoryRow key={h.id} h={h} />)}   {/* RATE HISTORY VOID: a void row struck through */}
                  </tbody>
                </ResizableTable>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setHistoryGrade(null)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      <PlanRequestSection />

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">{t("settings.team")}</CardTitle>
          <div className="flex gap-2">
          {canCrudUsers && (
            <Dialog open={newUserOpen} onOpenChange={setNewUserOpen}>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline" data-testid="create-user-btn">
                  <UserPlus className="me-1 h-4 w-4" /> Create user
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Create user (no email invite)</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label>Email</Label>
                    <Input type="email" data-testid="new-user-email" value={newUser.email}
                           onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} />
                  </div>
                  <div>
                    <Label>Name</Label>
                    <Input data-testid="new-user-name" value={newUser.name}
                           onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} />
                  </div>
                  <div>
                    <Label>Role</Label>
                    <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                      <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r] || r}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Billing Grade / Title <span className="text-[10px] text-muted-foreground">(separate from Role — used for costing)</span></Label>
                    <Select value={newUser.billing_grade || "__none__"}
                            onValueChange={(v) => setNewUser({ ...newUser, billing_grade: v === "__none__" ? "" : v })}>
                      <SelectTrigger data-testid="new-user-billing-grade"><SelectValue placeholder="— pick a grade —" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— None —</SelectItem>
                        {grades.map((g) => <SelectItem key={g.grade} value={g.grade}>{g.grade}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {grades.length === 0 && (
                      <div className="mt-1 text-[10px] text-amber-700">No grades defined yet. Add them in the Rate Card section below first.</div>
                    )}
                  </div>
                  <div>
                    <Label>{t("settings.newUserPasswordLabel")}</Label>
                    <Input data-testid="new-user-password" value={newUser.password}
                           onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                           placeholder={t("settings.newUserPasswordPlaceholder")} />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setNewUserOpen(false)}>Cancel</Button>
                  <Button onClick={createUserDirect} disabled={creatingUser}
                          className="bg-slate-900 hover:bg-slate-800" data-testid="new-user-save">
                    {creatingUser ? "Creating…" : "Create"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
          {canInvite && (
            <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-slate-900 hover:bg-slate-800" data-testid="invite-btn">
                  <UserPlus className="me-1 h-4 w-4" /> {t("settings.inviteMember")}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>{t("settings.inviteMember")}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label>{t("auth.email")}</Label>
                    <Input type="email" data-testid="invite-email" value={inviteForm.email} onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })} />
                  </div>
                  <div>
                    <Label>{t("settings.role")}</Label>
                    <Select value={inviteForm.role} onValueChange={(v) => setInviteForm({ ...inviteForm, role: v })}>
                      <SelectTrigger data-testid="invite-role"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r] || r}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="rounded-md bg-slate-50 p-2 text-xs text-muted-foreground">
                    {t("settings.inviteHint")}
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setInviteOpen(false)}>{t("common.cancel")}</Button>
                  <Button onClick={createInvite} disabled={creatingInvite || !inviteForm.email} className="bg-slate-900 hover:bg-slate-800" data-testid="invite-create">
                    {t("settings.createInvite")}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <ResizableTable cols={colW3} wrap className="w-full text-sm">
            <thead className="border-b bg-slate-50 text-xs uppercase text-muted-foreground">
              <tr>
                <ResizableTh index={0} className="px-4 py-2 text-start">Name</ResizableTh>
                <ResizableTh index={1} className="px-4 py-2 text-start">Email</ResizableTh>
                <ResizableTh index={2} className="px-4 py-2 text-start">Role</ResizableTh>
                <ResizableTh index={3} className="px-4 py-2 text-start">Billing Grade</ResizableTh>
                {canCrudUsers && <ResizableTh index={4} className="px-4 py-2 text-end">Actions</ResizableTh>}
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b" data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-2 font-medium">{u.name}</td>
                  <td className="px-4 py-2">{u.email}</td>
                  <td className="px-4 py-2"><span className="rounded-md bg-slate-100 px-2 py-0.5 text-xs" data-testid={`user-role-${u.id}`}>{u.title ? `${u.role} · ${u.title}` : u.role}</span></td>
                  <td className="px-4 py-2 text-xs" data-testid={`user-grade-${u.id}`}>
                    {u.billing_grade
                      ? <span className="rounded-md bg-emerald-50 px-2 py-0.5 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">{u.billing_grade}</span>
                      : <span className="text-muted-foreground">—</span>}
                  </td>
                  {canCrudUsers && (
                    <td className="px-4 py-2 text-end space-x-1">
                      <Button size="sm" variant="outline" data-testid={`user-edit-${u.id}`}
                              onClick={() => setEditingUser({ id: u.id, name: u.name, role: (u.role === "Partner" && u.title === "Director") ? "Director" : u.role, billing_grade: u.billing_grade || "", email: u.email })}>
                        Edit
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => reset2FA(u)}
                              title="Send an administrator-issued sign-in code (2FA reset)"
                              data-testid={`user-2fa-reset-${u.id}`}>
                        <KeyRound className="h-3.5 w-3.5 text-emerald-600" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteUser(u)}
                              disabled={u.id === user.id}
                              title={u.id === user.id ? "You cannot delete yourself" : "Delete user"}
                              data-testid={`user-delete-${u.id}`}>
                        <Trash2 className="h-3.5 w-3.5 text-red-600" />
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </ResizableTable>
        </CardContent>
      </Card>

      {/* Edit user dialog */}
      <Dialog open={!!editingUser} onOpenChange={(v) => !v && setEditingUser(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit user — {editingUser?.email}</DialogTitle></DialogHeader>
          {editingUser && (
            <div className="space-y-3">
              <div>
                <Label>Name</Label>
                <Input value={editingUser.name} data-testid="edit-user-name"
                       onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })} />
              </div>
              <div>
                <Label>Role</Label>
                <Select value={editingUser.role}
                        onValueChange={(v) => setEditingUser({ ...editingUser, role: v })}>
                  <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r] || r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Billing Grade / Title <span className="text-[10px] text-muted-foreground">(promotion? update this without touching Role)</span></Label>
                <Select value={editingUser.billing_grade || "__none__"}
                        onValueChange={(v) => setEditingUser({ ...editingUser, billing_grade: v === "__none__" ? "" : v })}>
                  <SelectTrigger data-testid="edit-user-billing-grade"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— None —</SelectItem>
                    {grades.map((g) => <SelectItem key={g.grade} value={g.grade}>{g.grade}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingUser(null)}>Cancel</Button>
            <Button onClick={saveUserEdit} disabled={savingUser}
                    className="bg-slate-900 hover:bg-slate-800" data-testid="edit-user-save">
              {savingUser ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {canCrudUsers && <SignInLocksPanel testid="signin-locks" />}   {/* Batch 22 · PR B · Follow-up 2 (item 43): the firm's active sign-in locks + Unlock */}

      {canManageInvites && (
        <Card>
          <CardHeader><CardTitle className="text-base">{t("settings.pendingInvites")}</CardTitle></CardHeader>
          <CardContent className="p-0">
            <ResizableTable cols={colW4} wrap className="w-full text-sm">
              <thead className="border-b bg-slate-50 text-xs uppercase text-muted-foreground">
                <tr>
                  <ResizableTh index={0} className="px-4 py-2 text-start">Email</ResizableTh>
                  <ResizableTh index={1} className="px-4 py-2 text-start">Role</ResizableTh>
                  <ResizableTh index={2} className="px-4 py-2 text-start">Status</ResizableTh>
                  <ResizableTh index={3} className="px-4 py-2 text-end">{t("common.actions")}</ResizableTh>
                </tr>
              </thead>
              <tbody>
                {invites.length === 0 && <tr><td colSpan={4} className="px-4 py-6 text-center text-muted-foreground">{t("settings.noInvites")}</td></tr>}
                {invites.map((i) => (
                  <tr key={i.id} className="border-b" data-testid={`invite-row-${i.id}`}>
                    <td className="px-4 py-2 font-medium">{i.email}</td>
                    <td className="px-4 py-2">{i.role}</td>
                    <td className="px-4 py-2">
                      <span className={`rounded-md px-2 py-0.5 text-xs ${i.status === "pending" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>{i.status}</span>
                      {i.email_sent === true && (
                        <span className="ms-2 rounded-md bg-emerald-50 px-2 py-0.5 text-[10px] text-emerald-700">{t("settings.emailSent")}</span>
                      )}
                      {i.email_sent === false && i.status === "pending" && (
                        <span className="ms-2 rounded-md bg-amber-50 px-2 py-0.5 text-[10px] text-amber-700" title={i.email_reason || ""} data-testid={`invite-not-sent-${i.id}`}>{t("settings.emailNotSent")}</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-end space-x-1">
                      {i.status === "pending" && (
                        <>
                          <Button size="sm" variant="ghost" onClick={() => resendInvite(i.id, i.email)} data-testid={`invite-resend-${i.id}`} title={t("settings.resendInvite").replace("{email}", i.email || "")}>
                            <RefreshCw className="me-1 h-3 w-3" /> {sendToLabel(i.email)}
                          </Button>
                          {i.token && (
                            <Button size="sm" variant="ghost" onClick={() => copyLink(i.token)} data-testid={`invite-copy-${i.id}`}>
                              <Copy className="me-1 h-3 w-3" /> {t("settings.copyLink")}
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" onClick={() => revoke(i.id)} data-testid={`invite-revoke-${i.id}`}>
                            <Trash2 className="h-3 w-3 text-red-600" />
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </ResizableTable>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

